import { zodResolver } from '@hookform/resolvers/zod';
import { format } from 'date-fns';
import { CalendarIcon, Save } from 'lucide-react';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { useRevalidateOnLanguageChange } from '@/i18n/use-revalidate-on-language-change';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import z from 'zod';
import { InspectionStatus, type InspectionType } from '@/common/inspection';
import { DeleteButton } from '@/components/DeleteButton';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
	Select,
	SelectContent,
	SelectGroup,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ROLE } from '../../../common/role';
import { usePermission } from '../../../hooks/use-permission';
import { cn, getInspectionStatusLabel } from '../../../lib/utils';
import { Restricted } from '../../Restricted';
import { Calendar } from '../../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Spinner } from '../../ui/spinner';
import { useNavigate } from 'react-router';

export type InspectionFormProps = {
	inspection: InspectionType | null;
	onSubmit: (data: InspectionType) => void;
	onDelete: () => void;
	isSubmitting: boolean;
	assetId: string;
	/** Restore of a deleted item previewed read-only (GH #331), where Delete sits on a live one */
	restoreButton?: React.ReactNode;
};

const buildFormSchema = (t: TFunction) =>
	z.object({
		isStandardInspection: z.boolean(),
		type: z.string(),
		reportNumber: z.string().min(1, { message: t('inspections.validation.reportNumberRequired') }),
		inspectorName: z.string().min(1, { message: t('inspections.validation.inspectorNameRequired') }),
		status: z.enum(Object.values(InspectionStatus)),
		startDate: z.date({ error: t('inspections.validation.startDateRequired') }),
		endDate: z.date().optional(),
		completionDate: z.date().optional(),
		assessmentAndActions: z.string().optional(),
		remarks: z.string().optional(),
	});

export function InspectionForm({
	inspection,
	onSubmit,
	onDelete,
	isSubmitting,
	assetId,
	restoreButton,
}: InspectionFormProps) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const formSchema = useMemo(() => buildFormSchema(t), [t]);

	const shouldUpdateInspections = usePermission(ROLE.INSPECTIONS_UPDATE);
	const form = useForm({
		disabled: !shouldUpdateInspections,
		resolver: zodResolver(formSchema),
		defaultValues: {
			isStandardInspection: inspection?.isStandardInspection || false,
			type: inspection?.type || '',
			reportNumber: inspection?.reportNumber || '',
			inspectorName: inspection?.inspectorName || '',
			status: inspection?.status || InspectionStatus.SCHEDULED,
			assessmentAndActions: inspection?.assessmentAndActions || '',
			remarks: inspection?.remarks || '',
		},
	});
	useRevalidateOnLanguageChange(form);

	// Populate form when inspection changes
	useEffect(() => {
		if (inspection) {
			form.reset({
				isStandardInspection: inspection?.isStandardInspection || false,
				type: inspection.type || '',
				reportNumber: inspection.reportNumber || '',
				inspectorName: inspection.inspectorName || '',
				status: inspection.status || InspectionStatus.SCHEDULED,
				startDate: inspection?.startDate ? new Date(inspection.startDate) : undefined,
				endDate: inspection.endDate ? new Date(inspection.endDate) : undefined,
				completionDate: inspection.completionDate ? new Date(inspection.completionDate) : undefined,
				assessmentAndActions: inspection.assessmentAndActions || '',
				remarks: inspection.remarks || '',
			});
		}
	}, [inspection, form.reset]);

	const handleSubmit = (data: z.infer<typeof formSchema>) => {
		onSubmit({
			...data,
			startDate: data.startDate.toISOString(),
			endDate: data.endDate ? data.endDate.toISOString() : null,
			completionDate: data.completionDate ? data.completionDate.toISOString() : null,
		});
	};

	const startDate = form.watch('startDate');
	const endDate = form.watch('endDate');

	// The page asks for confirmation (with what the delete takes along)
	const handleDeleteClick = () => onDelete();

	return (
		<Form {...form}>
			<form
				noValidate
				data-entity-form
				onSubmit={form.handleSubmit(handleSubmit)}
				onError={(errors) => {
					console.log(errors);
				}}
				onReset={() => form.reset()}
				className="space-y-6 max-w-full"
			>
				<Card className="rounded-lg text-card-foreground">
					<CardContent className="space-y-4">
						<div className="border-1 p-4 rounded-lg bg-input dark:bg-input/30">
							<FormField
								control={form.control}
								name="isStandardInspection"
								render={({ field }) => (
									<FormItem className="flex gap-3 items-center space-y-0">
										<FormControl>
											<Checkbox
												checked={field.value}
												onCheckedChange={field.onChange}
												disabled={field.disabled}
												className="h-4 w-4"
											/>
										</FormControl>
										<FormLabel>{t('inspections.form.standardInspection')}</FormLabel>
									</FormItem>
								)}
							/>
						</div>
						<div className="grid grid-cols-4 gap-3">
							<div className="col-span-2">
								<FormField
									control={form.control}
									name="type"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.type')}</FormLabel>
											<FormControl>
												<Select
													value={field.value}
													onValueChange={field.onChange}
													disabled={field.disabled}
												>
													<SelectTrigger className="w-full bg-white">
														<SelectValue placeholder="" />
													</SelectTrigger>
													<SelectContent className="bg-muted" position="popper">
														<SelectGroup>
															<SelectItem value="bauwerksprüfung">Bauwerksprüfung</SelectItem>
															<SelectItem value="bauwerksüberwachung">
																Bauwerksüberwachung
															</SelectItem>
															<SelectItem value="bauwerksbesichtigung">
																Bauwerksbesichtigung
															</SelectItem>
															<SelectItem value="sonderinspektion">Sonderinspektion</SelectItem>
														</SelectGroup>
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-2">
								<FormField
									control={form.control}
									name="status"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('common.labels.status')}</FormLabel>
											<FormControl>
												<Select
													value={field.value}
													onValueChange={field.onChange}
													disabled={field.disabled}
												>
													<SelectTrigger className="w-full bg-white">
														<SelectValue placeholder="" />
													</SelectTrigger>
													<SelectContent position="popper">
														{Object.values(InspectionStatus).map((value) => (
															<SelectItem key={value} value={value}>
																{getInspectionStatusLabel(value)}
															</SelectItem>
														))}
													</SelectContent>
												</Select>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>

						<div className="grid grid-cols-2 justify-between gap-3">
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="reportNumber"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('inspections.form.reportNumber')}</FormLabel>
											<FormControl>
												<Input type="text" {...field} className="bg-white" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="inspectorName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('inspections.form.inspector')}</FormLabel>
											<FormControl>
												<Input type="text" {...field} className="bg-white" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<div className="grid grid-cols-2 lg:grid-cols-3 justify-between gap-3">
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="startDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('inspections.form.startDate')}</FormLabel>
											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={cn(
																'w-full pl-3 text-left font-normal bg-white hover:bg-muted  border-border',
																!field.value && 'text-muted-foreground'
															)}
															disabled={field.disabled}
														>
															{field.value ? (
																format(field.value, 'yyyy-MM-dd')
															) : (
																<span>{t('inspections.form.pickDate')}</span>
															)}
															<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={field.onChange}
														disabled={(date) => date > new Date() || date < new Date('1900-01-01')}
														captionLayout="dropdown"
													/>
													<Button
														variant="ghost"
														size="sm"
														className="w-full rounded-t-none border-t border-border"
														onClick={() => field.onChange(null)}
													>
														{t('common.actions.clear')}
													</Button>
												</PopoverContent>
											</Popover>
											<FormControl></FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-1">
								<FormField
									control={form.control}
									name="endDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('inspections.form.endDate')}</FormLabel>
											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={cn(
																'w-full pl-3 text-left font-normal bg-white hover:bg-muted  border-border',
																!field.value && 'text-muted-foreground'
															)}
															disabled={field.disabled}
														>
															{field.value ? (
																format(field.value, 'yyyy-MM-dd')
															) : (
																<span>{t('inspections.form.pickDate')}</span>
															)}
															<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={field.onChange}
														disabled={(date) => !!startDate && date <= startDate}
														captionLayout="dropdown"
													/>
													<Button
														variant="ghost"
														size="sm"
														className="w-full rounded-t-none border-t border-border"
														onClick={() => field.onChange(undefined)}
													>
														{t('common.actions.clear')}
													</Button>
												</PopoverContent>
											</Popover>
											<FormControl></FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
							<div className="col-span-2 lg:col-span-1">
								<FormField
									control={form.control}
									name="completionDate"
									render={({ field }) => (
										<FormItem>
											<FormLabel>{t('inspections.form.completionDate')}</FormLabel>
											<Popover>
												<PopoverTrigger asChild>
													<FormControl>
														<Button
															variant={'outline'}
															className={cn(
																'w-full pl-3 text-left font-normal bg-white hover:bg-muted  border-border',
																!field.value && 'text-muted-foreground'
															)}
															disabled={field.disabled}
														>
															{field.value ? (
																format(field.value, 'yyyy-MM-dd')
															) : (
																<span>{t('inspections.form.pickDate')}</span>
															)}
															<CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
														</Button>
													</FormControl>
												</PopoverTrigger>
												<PopoverContent className="w-auto p-0" align="start">
													<Calendar
														mode="single"
														selected={field.value}
														onSelect={field.onChange}
														disabled={(date) => !!endDate && date <= endDate}
														captionLayout="dropdown"
													/>
													<Button
														variant="ghost"
														size="sm"
														className="w-full rounded-t-none border-t border-border"
														onClick={() => field.onChange(undefined)}
													>
														{t('common.actions.clear')}
													</Button>
												</PopoverContent>
											</Popover>
											<FormControl></FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
						<div>
							<FormField
								control={form.control}
								name="assessmentAndActions"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('inspections.form.assessmentAndActions')}</FormLabel>
										<FormControl>
											<Textarea {...field} className="bg-white" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
						<div>
							<FormField
								control={form.control}
								name="remarks"
								render={({ field }) => (
									<FormItem>
										<FormLabel>{t('inspections.form.remarks')}</FormLabel>
										<FormControl>
											<Textarea {...field} className="bg-white" />
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>
					</CardContent>

					<CardFooter className="border-t flex justify-between items-center border-border gap-2 pt-4">
						<div>
							{restoreButton}
							<Restricted to={ROLE.INSPECTIONS_DELETE}>
								{inspection?.id && (
									<DeleteButton
										onClick={handleDeleteClick}
										pending={isSubmitting}
										className="shadow-sm h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
									/>
								)}
							</Restricted>
						</div>
						<div className="flex gap-1.5 lg:gap-2">
							<Button
								type="reset"
								variant="outline"
								size="sm"
								className="h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3"
								onClick={() => navigate(`/assets/${assetId}?tab=inspections`)}
							>
								{t('common.actions.cancel')}
							</Button>
							<Restricted to={ROLE.INSPECTIONS_UPDATE}>
								<Button type="submit" size="sm" className="cursor-pointer h-7 lg:h-9 text-xs lg:text-sm px-2 lg:px-3" disabled={isSubmitting}>
									{!isSubmitting && <Save className="h-3.5 w-3.5 lg:h-4 lg:w-4" />}
									{!isSubmitting &&
										(inspection ? t('inspections.form.saveChanges') : t('inspections.form.createInspection'))}
									{isSubmitting && <Spinner className="h-3.5 w-3.5" />}
									{isSubmitting && (inspection ? t('common.states.saving') : t('inspections.form.creating'))}
								</Button>
							</Restricted>
						</div>
					</CardFooter>
				</Card>
			</form>
		</Form>
	);
}
